import { FC, MouseEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from 'antd';
import cs from 'classnames';
import { StatusCodes } from 'http-status-codes';

import DialogModal from 'components/DialogModal/DialogModal';
import FileViewModal from 'components/FileViewModal/FileViewModal';
import downloadFile from 'components/FileViewModal/utils/downloadFile';
import generateFileName from 'components/FileViewModal/utils/generateFileName';
import HTMLText from 'components/HTMLText/HTMLText';
import LoadingSpinner from 'components/LoadingSpinner';

import useErrorCatch from 'hooks/useErrorCatch';

import { del } from 'utils/api/api';
import fetchFileWithMimeType from 'utils/api/fetchFileWithMimeType';
import fetchTextWithAuth from 'utils/api/fetchTextWithAuth';
import isS3SignedUrl from 'utils/api/isS3SignedUrl';
import normalizeUrlEndpoint from 'utils/api/normalizeUrlEndpoint';
import { isAttachmentToDownload } from 'utils/isAttachmentToDownload';
import {
  isBlobObjectUrl,
  isDataUrl,
  scheduleRevokeObjectUrl
} from 'utils/urlUtils';

import { AttachmentTypes } from 'types/api';
import { Attachment as AttachmentModel } from 'types/commons';

import { RESOURCE } from 'constants/endpoints';
import { DialogModalTypes, MimeTypes, SpinnerSizes } from 'constants/enums';
import { NO_USER_PERMISSION_MESSAGE_TEXT } from 'constants/notifications';

import { ReactComponent as FileDownloadIcon } from 'assets/icons/fileDownload.svg';
import { ReactComponent as ImageIcon } from 'assets/icons/image.svg';
import { ReactComponent as DocumentIcon } from 'assets/icons/paper.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';

import styles from './Attachment.module.scss';

interface AttachmentProps {
  attachment: AttachmentModel<AttachmentTypes>;
  onRemoveFile?: () => void;
  isConfirmDelete?: boolean;
  isPreviewFile?: boolean;
  disabled?: boolean;
  isDeleteDisabled?: boolean;
  bottomLabel?: string | ReactNode | null;
}

const Attachment: FC<AttachmentProps> = ({
  attachment,
  onRemoveFile,
  isConfirmDelete = false,
  isPreviewFile = false,
  disabled = false,
  isDeleteDisabled = false,
  bottomLabel = null
}) => {
  const { t } = useTranslation();
  const { filename: name, sizeKb, createdDate, id, fileType } = attachment;

  const url = `${RESOURCE}/${id}`;
  const catchError = useErrorCatch();

  const [isRemoving, setIsRemoving] = useState<boolean>(false);
  const [isFileLoading, setIsFileLoading] = useState<boolean>(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] =
    useState<boolean>(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false);

  const triggerFileDownload = (fileData: string, fileName: string) => {
    downloadFile({
      catchError,
      dataUrl: fileData,
      name: fileName,
      translation: t
    });
  };

  const iconClassName = disabled ? 'icon-disabled' : 'icon-enabled';

  const ATTACHMENT_ICONS: Record<MimeTypes, JSX.Element> = {
    [MimeTypes.doc]: <DocumentIcon className={iconClassName} />,
    [MimeTypes.docx]: <DocumentIcon className={iconClassName} />,
    [MimeTypes.pdf]: <DocumentIcon className={iconClassName} />,
    [MimeTypes.jpeg]: <ImageIcon className={iconClassName} />,
    [MimeTypes.png]: <ImageIcon className={iconClassName} />
  };

  const icon = ATTACHMENT_ICONS[fileType] || null;

  const deleteFile = async () => {
    if (disabled) {
      return;
    }

    try {
      setIsRemoving(true);
      await del(url);
      if (onRemoveFile) {
        onRemoveFile();
      }
    } catch (error) {
      catchError({
        error,
        default: { message: t('error.message.deletingFile') },
        additionalErrorMessageMap: {
          [StatusCodes.FORBIDDEN]: {
            message: t(NO_USER_PERMISSION_MESSAGE_TEXT)
          }
        }
      });
    } finally {
      setIsRemoving(false);
    }
  };

  const resolveSignedFileUrl = async (): Promise<string> => {
    const rawUrl = (attachment.url || '').trim();
    const isS3Url = isS3SignedUrl(rawUrl);
    const isBlobUrl = isBlobObjectUrl(rawUrl);
    const isDataUrlValue = isDataUrl(rawUrl);

    if (rawUrl && (isS3Url || isBlobUrl || isDataUrlValue)) {
      return rawUrl;
    }

    const endpoint = rawUrl || `${RESOURCE}/${id}/url`;
    return fetchTextWithAuth(normalizeUrlEndpoint(endpoint));
  };

  const handleFileDownload = async (e?: MouseEvent) => {
    e?.stopPropagation();

    if (disabled) {
      return;
    }

    try {
      setIsFileLoading(true);

      const signedUrl = await resolveSignedFileUrl();

      if (isS3SignedUrl(signedUrl)) {
        triggerFileDownload(
          signedUrl,
          name || generateFileName(signedUrl, fileType)
        );
        return;
      }

      const { blob, fileType: loadedFileType } = await fetchFileWithMimeType(
        signedUrl,
        true
      );

      const objectUrl = window.URL.createObjectURL(blob);
      triggerFileDownload(
        objectUrl,
        name || generateFileName(signedUrl, loadedFileType ?? fileType)
      );
      scheduleRevokeObjectUrl(objectUrl);
    } catch (error) {
      catchError({
        error,
        default: { message: t('error.message.downloadingFile') },
        additionalErrorMessageMap: {
          [StatusCodes.FORBIDDEN]: {
            message: t(NO_USER_PERMISSION_MESSAGE_TEXT)
          }
        }
      });
    } finally {
      setIsFileLoading(false);
    }
  };

  const handleFilePreviewOrDownload = async (e: MouseEvent) => {
    e.stopPropagation();

    if (disabled) {
      return;
    }

    if (!isPreviewFile || isAttachmentToDownload(attachment)) {
      await handleFileDownload();
      return;
    }

    setIsPreviewModalOpen(true);
  };

  const handleFilePreviewModalClose = () => {
    setIsPreviewModalOpen(false);
  };

  const handleFileRemove = async (event: MouseEvent<SVGSVGElement>) => {
    event.stopPropagation();

    if (isConfirmDelete) {
      setIsConfirmDeleteModalOpen(true);
      return;
    }

    deleteFile();
  };

  const handleDeleteConfirm = () => {
    setIsConfirmDeleteModalOpen(false);
    deleteFile();
  };

  const handleDeleteReject = () => {
    setIsConfirmDeleteModalOpen(false);
  };

  return (
    <>
      {!isRemoving ? (
        <div
          className={cs(styles.attachment, {
            [styles.attachmentLoading]: isFileLoading,
            [styles.attachmentDisabled]: disabled
          })}
          onClick={handleFilePreviewOrDownload}
        >
          <div className={styles.attachmentContent}>
            {isFileLoading ? (
              <div className={styles.attachmentSpinner}>
                <LoadingSpinner size={SpinnerSizes.Medium} />
              </div>
            ) : (
              <div className={styles.attachmentIcon}>{icon}</div>
            )}

            <div className={styles.attachmentData}>
              <div
                className={
                  disabled
                    ? styles.attachmentNameDisabled
                    : styles.attachmentName
                }
              >
                {name}
              </div>
              <div
                className={
                  disabled
                    ? styles.attachmentSizeDisabled
                    : styles.attachmentSize
                }
              >
                {`${createdDate}  ${sizeKb} kb`}
              </div>
            </div>
            <FileDownloadIcon
              onClick={handleFileDownload}
              className={iconClassName}
            />

            {!isDeleteDisabled && (
              <TrashIcon
                onClick={handleFileRemove}
                className={disabled ? 'icon-trash-disabled' : 'icon-trash'}
              />
            )}
          </div>
          <div className={styles.attachmentLabel}>{bottomLabel}</div>
        </div>
      ) : (
        <Skeleton.Input active size="large" block />
      )}
      {isConfirmDeleteModalOpen && (
        <DialogModal
          type={DialogModalTypes.Delete}
          isOpen={isConfirmDeleteModalOpen}
          onConfirm={handleDeleteConfirm}
          onClose={handleDeleteReject}
          description={
            <HTMLText text={t('modal.deleteFile.areYouSure', { name })} />
          }
        />
      )}
      {isPreviewModalOpen && (
        <FileViewModal
          isOpen
          onClose={handleFilePreviewModalClose}
          title={t('modal.filePreview.title', { name })}
          attachment={attachment}
        />
      )}
    </>
  );
};

export default Attachment;
